import React from 'react';
// template components
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
// icons
import PDF from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/Pdf';
// styles
import {
  DivStyledLinksContainer,
  DocumentLinkStyled,
  HeadingWithLineStyled,
} from './styles';

const pdfBaseUrl = '/assets/pdf/financial-reports/results/';

// PDFs - keep these PDF file names in order of appearance on the page to reduce cognitive load and make it easier to update
// 2024 Annual Statements
const consolidatedFinancialStatements2024 = 'consolidated-financial-statements.pdf';
const statutoryFinancialStatementsNm2024 = 'nm-annual-statement.pdf';
const statutoryFinancialStatementsLtc2024 = 'nltc-annual-statement.pdf';
// quarterly statements - NM
//  - 2025
const resultsSummaryBalanceSheet2025FirstQuarter = 'q1-2025-balance-sheet-and-income-statement.pdf';
const statutoryStatement2025FirstQuarter = 'nm-q1-2025-statement-final-no-schedules.pdf';
// - 2024
const resultsSummaryBalanceSheet2024ThirdQuarter = 'q3-2024-balance-sheet-and-income-statement.pdf';
const statutoryStatement2024ThirdQuarter = 'nm-q3-2024-statement-final-no-schedules.pdf';
const resultsSummaryBalanceSheet2024SecondQuarter = 'q2-2024-balance-sheet-and-income-statement.pdf';
const statutoryStatement2024SecondQuarter = 'nm-q2-2024-statement-final-no-schedules.pdf';
// quarterly statements - LTC
//  - 2025
const statutoryStatementLtc2025FirstQuarter = 'nm-q1-2025-statement-ltc-final-no-schedules.pdf';
// - 2024
const statutoryStatementLtc2024ThirdQuarter = 'nltc-q3-2024-statement-final-no-schedules.pdf';
const statutoryStatementLtc2024SecondQuarter = 'nltc-q2-2024-statement-final-no-schedules.pdf';

const data = [
  {
    groupId: 'financial-information-annual-statements',
    headingText: 'Annual Statements',
    subGroups: [
      {
        subGroupId: 'annual-statements-2024',
        subHeadingText: '2024 Annual Statements',
        links: [
          {
            linkId: 'financial-information-2024-audited-financial-statements',
            linkText: '2024 Audited Financial Statements',
            linkUrl: `${pdfBaseUrl}${consolidatedFinancialStatements2024}`,
            linkAriaLabel: 'Download the 2024 Audited financial statements',
          },
          {
            linkId: 'financial-information-2024-life-insurance-company-annual-statement',
            linkText: '2024 Statutory Annual Statement for Northwestern Mutual Life Insurance Company',
            linkUrl: `${pdfBaseUrl}${statutoryFinancialStatementsNm2024}`,
            linkAriaLabel: 'Download the 2024 annual statement for Northwestern Mutual Life Insurance Company',
          },
          {
            linkId: 'financial-information-2024-long-term-care-insurance-company-annual-statement',
            linkText: '2024 Statutory Annual Statement for Northwestern Long Term Care Insurance Company',
            linkUrl: `${pdfBaseUrl}${statutoryFinancialStatementsLtc2024}`,
            linkAriaLabel: 'Download the 2024 annual statement for Northwestern Long Term Care Insurance Company',
          },
        ],
      },
    ],
  },
  {
    groupId: 'financial-information-nm-quarterly-statements',
    headingText: 'Statutory Quarterly Statements for Northwestern Mutual Life Insurance Company',
    subGroups: [
      {
        subGroupId: 'statutory-quarterly-statements-nm-2025',
        subHeadingText: '2025 Quarterly Statements',
        links: [
          {
            linkId: 'financial-information-2025-first-quarter-balance-sheet-and-income-statement',
            linkText: 'First Quarter Summary of Financial Results',
            linkUrl: `${pdfBaseUrl}${resultsSummaryBalanceSheet2025FirstQuarter}`,
            linkAriaLabel: 'Download the 2025 first quarter Audited financial statements, balance sheet, and income statement',
          },
          {
            linkId: 'financial-information-2025-first-quarter-statutory-annual-statement',
            linkText: 'First Quarter Statutory Statement',
            linkUrl: `${pdfBaseUrl}${statutoryStatement2025FirstQuarter}`,
            linkAriaLabel: 'Download the 2025 first quarter statutory annual statement for Northwestern Mutual Life Insurance Company',
          },
        ],
      },
      {
        subGroupId: 'statutory-quarterly-statements-nm-2024',
        subHeadingText: '2024 Quarterly Statements',
        links: [
          {
            linkId: 'financial-information-2024-third-quarter-balance-sheet-and-income-statement',
            linkText: 'Third Quarter Summary of Financial Results',
            linkUrl: `${pdfBaseUrl}${resultsSummaryBalanceSheet2024ThirdQuarter}`,
            linkAriaLabel: 'Download the 2024 third quarter Audited financial statements, balance sheet, and income statement',
          },
          {
            linkId: 'financial-information-2024-third-quarter-statutory-annual-statement',
            linkText: 'Third Quarter Statutory Statement',
            linkUrl: `${pdfBaseUrl}${statutoryStatement2024ThirdQuarter}`,
            linkAriaLabel: 'Download the 2024 third quarter statutory annual statement for Northwestern Mutual Life Insurance Company',
          },
          {
            linkId: 'financial-information-2024-second-quarter-balance-sheet-and-income-statement',
            linkText: 'Second Quarter Summary of Financial Results',
            linkUrl: `${pdfBaseUrl}${resultsSummaryBalanceSheet2024SecondQuarter}`,
            linkAriaLabel: 'Download the 2024 second quarter Audited financial statements, balance sheet, and income statement',
          },
          {
            linkId: 'financial-information-2024-second-quarter-statutory-annual-statement',
            linkText: 'Second Quarter Statutory Statement',
            linkUrl: `${pdfBaseUrl}${statutoryStatement2024SecondQuarter}`,
            linkAriaLabel: 'Download the 2024 second quarter statutory annual statement for Northwestern Mutual Life Insurance Company',
          },
        ],
      },
    ],
  },
  {
    groupId: 'financial-information-ltc-quarterly-statements',
    headingText: 'Statutory Quarterly Statements for Northwestern Long Term Care Insurance Company',
    subGroups: [
      {
        subGroupId: 'statutory-quarterly-statements-ltc-2025',
        subHeadingText: '2025 Quarterly Statements',
        links: [
          {
            linkId: 'financial-information-2025-first-quarter-statutory-quarterly-statement',
            linkText: 'First Quarter Statutory Statement',
            linkUrl: `${pdfBaseUrl}${statutoryStatementLtc2025FirstQuarter}`,
            linkAriaLabel: 'Download the 2025 first quarter statutory quarterly statement for Northwestern Long Term Care Insurance Company',
          },
        ],
      },
      {
        subGroupId: 'statutory-quarterly-statements-ltc-2024',
        subHeadingText: '2024 Quarterly Statements',
        links: [
          {
            linkId: 'financial-information-third-quarter-statutory-statement-2024',
            linkText: 'Third Quarter Statutory Statement',
            linkUrl: `${pdfBaseUrl}${statutoryStatementLtc2024ThirdQuarter}`,
            linkAriaLabel: 'Download the 2024 third quarter statutory annual statement for Northwestern Long Term Care Insurance Company',
          },
          {
            linkId: 'financial-information-second-quarter-statutory-statement-2024',
            linkText: 'Second Quarter Statutory Statement',
            linkUrl: `${pdfBaseUrl}${statutoryStatementLtc2024SecondQuarter}`,
            linkAriaLabel: 'Download the 2024 second quarter statutory annual statement for Northwestern Long Term Care Insurance Company',
          },
        ],
      },
    ],
  },
];

export default () => (
  <SectionContainer
    id='financial-information-financial-statements'
    disableBottomPadding>
    <Row align='center'>
      <Col medium={8}>
        <Typography
          component='h2'
          align='center'
          nextElement='typography'>
          Our Results
        </Typography>
        {data.map((item) => (
          <React.Fragment key={item.groupId}>
            <Typography
              component='h3'
              nextElement='graphic'>
              {item.headingText}
            </Typography>
            {item.subGroups && item.subGroups.map((subGroup) => (
              <React.Fragment key={`${subGroup.subGroupId}`}>
                <HeadingWithLineStyled
                  component='h4'
                  headingText={subGroup.subHeadingText}
                />
                <DivStyledLinksContainer>
                  {subGroup.links && subGroup.links.map((link) => (
                    <DocumentLinkStyled
                      key={link.linkId}
                      linkId={link.linkId}
                      linkUrl={link.linkUrl}
                      iconObject={PDF}
                      isInternalPdf
                      linkAriaLabel={link.linkAriaLabel}>
                      {link.linkText}
                    </DocumentLinkStyled>
                  ))}
                </DivStyledLinksContainer>
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </Col>
    </Row>
  </SectionContainer>
);
